/** ***********************************************************************
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ************************************************************************* */

/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';
import Heading from '@react/react-spectrum/Heading';
import Tooltip from '@react/react-spectrum/Tooltip';
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger';
import classnames from 'classnames';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import parser from 'html-react-parser';
import uuid from 'uuid';
import TranslationUtil from '../../../../../utils/translation';
import { appTileAnalytics } from '../../../../../analytics';
import { ANALYTICS } from '../../../../../utils/enums';
import { ACTION_ID } from '../../../../../utils/modelMapper/appModelMapper/appModelConstants';
import { APP_SECTION_LAYOUTS } from '../../../../layoutConstants';
import './index.scss';
import iconMapping from '../../../../../utils/iconMapping';
import { getContentExt } from '../../../../../utils/commonUtil';
import CustomIcon from '../../../../../components/sidenav/customIcon/customIcon';
import ContentParser from '../../../../../utils/contentParser';

export default @observer class AppTileHeader extends Component {
    @observable height = 0;

    @action
    setRelativeHeight(updatedHeight) {
        this.height = updatedHeight;
    }

    onTileHeaderClick = (e) => {
        e.stopPropagation();
        const {
            app,
            appSectionFilter,
            layoutId,
            cardSize,
            appSectionBasedFields: {
                isCategorySection,
            },
            mediaList,
        } = this.props;
        const contentSize = cardSize === '33-percent' ? '33' : '50';
        const guid = uuid.v4();
        app.onAppAction(ACTION_ID.APP_TILE_CLICK, app, { guid, isCategorySection });
        appTileAnalytics(ANALYTICS.EVENTS.APP_TILE_CLICK, {
            app,
            appSectionFilter,
            actionId: app.actionIds.select,
            guid,
            viewType: ANALYTICS.VIEW_TYPE[layoutId],
            contentSize,
            contentExt: getContentExt(mediaList),
        });
    }

    handleIconLoadError(app, backUpIcon) {
        if (backUpIcon) {
            app.setIconUrl(backUpIcon);
        }
    }

    renderTagline(localizedTagline) {
        if (!localizedTagline) {
            return [];
        }

        return (
            <Heading
                className="mediaCardView-appTile-tagline"
                variant="subtitle3">
                {localizedTagline}
            </Heading>
        );
    }

    renderKeyFeaturesList(appKeyFeatures) {
        if (!appKeyFeatures) return null;
        return (
            <ul className="mediaCardView-appTile-keyFeature-list">
                {appKeyFeatures.map((keyFeature) => {
                    if (!keyFeature) {
                        return null;
                    }
                    const {
                        icon: {
                            spectrumID,
                            icon: iconFile,
                        } = {},
                    } = keyFeature;
                    const iconUrl = ContentParser.getAssetPath(iconFile);
                    const Icon = spectrumID
                        && iconMapping[spectrumID]
                        ? iconMapping[spectrumID]
                        : null;
                    /* eslint-disable no-nested-ternary */
                    return (
                        <li className="mediaCardView-appTile-keyFeature">
                            {Icon ? <Icon size="S" /> : iconUrl
                                ? <CustomIcon imgSrc={iconUrl} /> : null}
                            {keyFeature.keyFeatureText && keyFeature.keyFeatureText.html
                                // eslint-disable-next-line max-len
                                ? <p dangerouslySetInnerHTML={{ __html: keyFeature.keyFeatureText.html }} />
                                : parser(documentToHtmlString(keyFeature.keyFeatureText))}
                        </li>
                    );
                    /* eslint-enable no-nested-ternary */
                })}
            </ul>
        );
    }

    renderHeaderText({
        app, isHeaderClickable, sectionHeader,
        appSectionFilter,
        subSectionFilter, updateTileVersion,
    }) {
        const {
            iconURL, tileVersion, appFamilyName, platform,
            isPDPDataAvailable, backUpIcon, sapcode, isSupportedForLongTerm,
        } = app;
        return (
            <div
                className={classnames('mediaCardView-appTile-headerName-wrapper',
                    { clickable: (isPDPDataAvailable || app.isLaunchable) })}
                onClick={((isPDPDataAvailable || app.isLaunchable)
                    && !isHeaderClickable) ? this.onTileHeaderClick : undefined}>
                <img
                    className="mediaCardView-appTile-headerName-wrapper-icon"
                    src={iconURL}
                    onError={() => this.handleIconLoadError(app, backUpIcon)}
                    alt={appFamilyName} />
                <div className="mediaCardView-appTile-headerName-wrapper-text">
                    <div className="tile-version-wrapper">
                        <OverlayTrigger
                            disabled={!tileVersion}
                            placement="top"
                            trigger={['hover', 'focus']}
                            delayShow={500}>
                            <span className="mediaCardView-appTile-primaryText">
                                {appFamilyName}
                            </span>
                            <Tooltip className="mediaCardView-appTile-headerName-version-tooltip">
                                <span>
                                    {
                                        isSupportedForLongTerm
                                            ? TranslationUtil.getMessage('ccx.appTile.sapCode.tooltip.version.lts', { version: tileVersion, ltsStr: TranslationUtil.getMessage('ccx.otherVersions.info.longTermSupported') })
                                            : TranslationUtil.getMessage('ccx.appTile.sapCode.tooltip.version', { version: tileVersion })
                                    }
                                </span>
                            </Tooltip>
                        </OverlayTrigger>
                        {updateTileVersion
                            && (
                                <span className="mediaCardView-appTile-codexVersion">
                                    {TranslationUtil.getMessage('ccx.appTile.version-prefix') + updateTileVersion}
                                </span>
                            )}
                        { !updateTileVersion && app.doesMultipleVersionsExistInSection(sapcode,
                            appSectionFilter, subSectionFilter, sectionHeader)
                            && (
                                <span className="mediaCardView-appTile-codexVersion">
                                    {TranslationUtil.getMessage('ccx.appTile.version-prefix') + tileVersion}
                                </span>
                            )
                        }
                    </div>
                    <div className="tile-platform">
                        {TranslationUtil.getMessage(`ccx.appTile.platformIcons.${platform}.tooltip`)}
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const {
            app: { isPDPDataAvailable }, app, appSectionBasedFields, appSectionFilter,
            sectionHeader, subSectionFilter, layoutId, headerBasedAppData,
        } = this.props;
        const {
            subText, localizedTagline, isHeaderClickable, updateTileVersion,
        } = appSectionBasedFields;

        const { tagLine, appKeyFeatures } = headerBasedAppData;
        return (
            <div
                className={classnames('mediaCardView-appTile-header-container', {
                    clickable: (isHeaderClickable
                    && (isPDPDataAvailable || app.isLaunchable)),
                })}
                onClick={isHeaderClickable && (isPDPDataAvailable || app.isLaunchable)
                    ? this.onTileHeaderClick : undefined}>
                <div className="mediaCardView-appTile-header">
                    {this.renderHeaderText({
                        app,
                        subText,
                        isHeaderClickable,
                        sectionHeader,
                        appSectionFilter,
                        subSectionFilter,
                        updateTileVersion,
                    })}
                </div>
                <div className="tagLine-update-container">
                    {this.renderTagline(tagLine || localizedTagline)}
                </div>
                {layoutId === APP_SECTION_LAYOUTS.CARD_MEDIA_TAGLINE_FEATURE_VIEW
                    && this.renderKeyFeaturesList(appKeyFeatures)}
            </div>
        );
    }
}
